import React, { useContext, useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { UserStateContext } from 'context/user-state-context';
import { getTaskStages } from '../../shared/common.api';

/**
 * CIN-6288: single-select of the company's task stages (ordered by sort_order).
 * value/onChange use the stage id. Stages are a small managed list, so this uses
 * a plain MUI Autocomplete rather than the search-backed DynamicTypeAheadMui.
 *
 * CIN-7073: `onStagesLoaded(stages)` hands the loaded list to the parent so the
 * create modal can say which stage default deadline will apply without fetching the
 * stages itself.
 *
 * CIN-7070: `boardId` limits the options to that board's stages (null = every stage,
 * which is the one board for a single-board company). When the board changes and
 * the current value isn't one of the new board's stages, the value is cleared —
 * before `onStagesLoaded` runs, so a parent that then picks a stage (the detail
 * modal picks the new board's first open stage) has the last word.
 */
export const StageSelect = ({
  value,
  onChange,
  label = 'Stage',
  disabled,
  onStagesLoaded,
  boardId = null,
  helperText
}) => {
  const {
    asCompany
  } = useContext(UserStateContext);
  const [stages, setStages] = useState([]);
  useEffect(() => {
    if (!asCompany?.id) return undefined;
    let cancelled = false;
    getTaskStages({
      company: asCompany.id,
      limit: 999,
      ...(boardId ? {
        board: boardId
      } : {})
    }).then(({
      data
    }) => {
      if (cancelled) return;
      const results = data.results || [];
      setStages(results);
      if (value && !results.some(s => s.id === value)) onChange(null);
      if (onStagesLoaded) onStagesLoaded(results);
    });
    return () => {
      cancelled = true;
    };
    // Refetch only when the company or board changes: value and the callback props
    // change on every parent render, and refetching then would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asCompany?.id, boardId]);
  const selected = stages.find(s => s.id === value) || null;
  return <Autocomplete options={stages} getOptionLabel={option => option.name || ''} isOptionEqualToValue={(option, val) => option.id === val.id} value={selected} onChange={(_e, val) => onChange(val ? val.id : null)} disabled={disabled} renderInput={params => <TextField {...params} label={label} helperText={helperText} />} sx={{
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px'
    }
  }} />;
};